import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  BeakerIcon,
  DocumentArrowDownIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { aiAPI, downloadFile, formatError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const AIAnalysis = () => {
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [analysisParams, setAnalysisParams] = useState({
    analysis_period_days: 30
  });

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      const response = await aiAPI.getReports();
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to load AI reports');
      console.error('Load reports error:', error);
    } finally {
      setReportsLoading(false);
    }
  };

  const triggerAnalysis = async () => {
    setLoading(true);
    try {
      toast.loading('Running AI analysis...', { id: 'ai-analysis' });

      const response = await aiAPI.analyze(analysisParams);

      toast.success('AI analysis completed successfully!', { id: 'ai-analysis' });

      // Reload reports to show the new one
      loadReports();

      // Show summary
      const summary = response.data.analysis_data.summary;
      toast.success(
        `Analysis complete: ${summary.items_needing_reorder} items need reordering (${summary.high_priority_items} high priority)`,
        { duration: 5000 }
      );

    } catch (error) {
      toast.error(formatError(error), { id: 'ai-analysis' });
    } finally {
      setLoading(false);
    }
  };

  const viewReport = async (reportId) => {
    try {
      const response = await aiAPI.getReportDetails(reportId);
      setSelectedReport(response.data);
    } catch (error) {
      toast.error('Failed to load report details');
    }
  };

  const downloadReport = async (reportId, reportName) => {
    try {
      toast.loading('Downloading report...', { id: 'download' });

      const response = await aiAPI.downloadReport(reportId);
      const filename = `${reportName.replace(/\s+/g, '_')}.xlsx`;
      downloadFile(response.data, filename);

      toast.success('Report downloaded', { id: 'download' });
    } catch (error) {
      toast.error('Download failed', { id: 'download' });
    }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }

    try {
      await aiAPI.deleteReport(reportId);
      setReports(reports.filter(report => report.id !== reportId));

      if (selectedReport && selectedReport.report.id === reportId) {
        setSelectedReport(null);
      }

      toast.success('Report deleted');
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH': return 'text-red-600 bg-red-50 border-red-200';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'LOW': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">AI Analysis</h1>
        <p className="text-gray-600 mt-1">Get intelligent insights and reorder recommendations</p>
      </div>

      {/* Analysis Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Analysis Period
            </label>
            <select
              value={analysisParams.analysis_period_days}
              onChange={(e) => setAnalysisParams({
                ...analysisParams,
                analysis_period_days: parseInt(e.target.value)
              })}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>

          <button
            onClick={triggerAnalysis}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <LoadingSpinner size="sm" text="" center={false} />
            ) : (
              <BeakerIcon className="h-4 w-4 mr-2" />
            )}
            Run AI Analysis
          </button>
        </div>
      </div>

      {/* Reports List and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Reports List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Recent Reports</h2>
            </div>

            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {reportsLoading ? (
                <div className="p-4">
                  <LoadingSpinner size="sm" text="Loading reports..." />
                </div>
              ) : reports.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <BeakerIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>No reports yet. Run your first analysis!</p>
                </div>
              ) : (
                reports.map((report) => (
                  <div
                    key={report.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${selectedReport?.report.id === report.id ? 'bg-blue-50 border-r-2 border-blue-600' : ''
                      }`}
                    onClick={() => viewReport(report.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {report.report_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      </div>

                      <div className="flex items-center space-x-2">
                        {report.generated_file_path && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadReport(report.id, report.report_name);
                            }}
                            className="text-green-600 hover:text-green-800"
                            title="Download Excel report"
                          >
                            <DocumentArrowDownIcon className="h-4 w-4" />
                          </button>
                        )}

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteReport(report.id);
                          }}
                          className="text-red-600 hover:text-red-800"
                          title="Delete report"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${report.status === 'completed' ? 'bg-green-100 text-green-800' :
                          report.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                        {report.status === 'completed' ?
                          <CheckCircleIcon className="h-3 w-3 mr-1" /> :
                          <ClockIcon className="h-3 w-3 mr-1" />
                        }
                        {report.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Report Details */}
        <div className="lg:col-span-2">
          {selectedReport ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900">
                    {selectedReport.report.report_name}
                  </h2>

                  {selectedReport.has_excel_file && (
                    <button
                      onClick={() => downloadReport(selectedReport.report.id, selectedReport.report.report_name)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                      Download Excel
                    </button>
                  )}
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-600">Items Analyzed</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {selectedReport.analysis_data.total_items_analyzed}
                    </div>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-red-600">Need Reordering</div>
                    <div className="text-2xl font-bold text-red-900">
                      {selectedReport.analysis_data.items_needing_reorder}
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-blue-600">Estimated Cost</div>
                    <div className="text-2xl font-bold text-blue-900">
                      {formatCurrency(selectedReport.analysis_data.total_estimated_reorder_cost)}
                    </div>
                  </div>
                </div>

                {/* Priority Breakdown */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Priority Breakdown</h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-red-600">
                        {selectedReport.analysis_data.summary.high_priority_items}
                      </div>
                      <div className="text-sm text-gray-600">High Priority</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-yellow-600">
                        {selectedReport.analysis_data.summary.medium_priority_items}
                      </div>
                      <div className="text-sm text-gray-600">Medium Priority</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-green-600">
                        {selectedReport.analysis_data.summary.low_priority_items}
                      </div>
                      <div className="text-sm text-gray-600">Low Priority</div>
                    </div>
                  </div>
                </div>

                {/* Recommendations Table */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Reorder Recommendations</h3>
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Product
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Current Stock
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Recommended Qty
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Priority
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Est. Cost
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedReport.analysis_data.recommendations
                          .filter(rec => rec.reorder_needed)
                          .slice(0, 10)
                          .map((rec, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {rec.product_name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {rec.product_id}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {rec.current_stock}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {rec.suggested_reorder_quantity}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(rec.priority)}`}>
                                  {rec.priority}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(rec.estimated_cost)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Download Excel Recommendation */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border-2 border-green-200">
                  <div className="flex items-start">
                    <DocumentArrowDownIcon className="h-8 w-8 text-green-600 mr-4 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        📊 Complete Analysis Available in Excel
                      </h3>
                      <p className="text-sm text-gray-700 mb-4">
                        Download the comprehensive Excel report for detailed insights including:
                      </p>
                      <ul className="text-sm text-gray-700 space-y-2 mb-4">
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2">✓</span>
                          <span>Complete reorder recommendations with quantities</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2">✓</span>
                          <span>Color-coded priority levels (High/Medium/Low)</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2">✓</span>
                          <span>Cost estimates and supplier information</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2">✓</span>
                          <span>Summary statistics and analysis metrics</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2">✓</span>
                          <span>Easy-to-read spreadsheet format for hotel managers</span>
                        </li>
                      </ul>
                      {selectedReport.has_excel_file && (
                        <button
                          onClick={() => downloadReport(selectedReport.report.id, selectedReport.report.report_name)}
                          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-lg"
                        >
                          <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                          Download Excel Report
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <ChartBarIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Select a Report
              </h3>
              <p className="text-gray-600">
                Choose a report from the left panel to view detailed analysis and recommendations.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIAnalysis;